import React, { useRef, useState, useEffect, useMemo } from "react";
import { CameraIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { authAPI } from "../../../api/authApi";
import { useAuth } from "../../../contexts/AuthContext";
import AvatarCropModal from "../../settings/modals/AvatarCropModal";
import Spinner from "../../../ui/Spinner";

export default function WelcomeStep({ onNext, onSkip, onBack, showBack }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cropUrl, setCropUrl] = useState(null);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const { user, syncUser } = useAuth();

  const cropUrlRef = useRef(null);
  cropUrlRef.current = cropUrl;
  useEffect(() => {
    return () => {
      if (cropUrlRef.current) URL.revokeObjectURL(cropUrlRef.current);
    };
  }, []);

  const initials = useMemo(() => {
    if (!user?.name) return "";
    const parts = user.name.split(" ");
    return (parts[0]?.[0] + (parts[1]?.[0] || "")).toUpperCase();
  }, [user?.name]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setCropUrl(URL.createObjectURL(file));
    e.target.value = "";
  };

  const handleCropCancel = () => {
    if (cropUrl) URL.revokeObjectURL(cropUrl);
    setCropUrl(null);
  };

  const handleCropSave = async (croppedBlob) => {
    if (cropUrl) URL.revokeObjectURL(cropUrl);
    setCropUrl(null);

    const previewUrl = URL.createObjectURL(croppedBlob);
    setUploadedUrl(previewUrl);
    setUploading(true);

    try {
      const response = await authAPI.uploadAvatar(croppedBlob);
      setUploadedUrl(response.data.photoUrl);
      syncUser({ avatar: response.data.photoUrl });
    } catch {
      setUploadedUrl(null);
      setUploadError("Failed to upload photo. Please try again.");
    } finally {
      URL.revokeObjectURL(previewUrl);
      setUploading(false);
    }
  };

  const displayAvatar = uploadedUrl || user?.avatar;

  return (
    <div className="flex flex-col items-center text-center">
      {/* Back button */}
      {showBack && (
        <button
          onClick={onBack}
          className="self-start -mt-2 mb-2 p-1 rounded-md text-white/60 hover:text-white/80"
          aria-label="Go back"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
      )}

      <h2 className="text-2xl font-bold text-white mb-2">
        Welcome to Traces!
      </h2>
      <p className="text-white/70 text-sm mb-8 max-w-xs">
        Let's get you set up. Start by adding a profile photo so others can
        recognize you.
      </p>

      {/* Avatar display */}
      <div className="relative mb-4">
        <div className="h-28 w-28 rounded-full overflow-hidden border-2 border-primary bg-surface-sunken flex-shrink-0">
          {displayAvatar ? (
            <img
              src={displayAvatar}
              alt="Avatar"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full grid place-items-center bg-white/15 text-primary text-2xl font-semibold">
              {initials}
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
              <Spinner size={24} className="text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Upload button */}
      <button
        type="button"
        disabled={uploading}
        onClick={() => fileInputRef.current?.click()}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/15 border border-white/25 text-white text-sm hover:bg-white/25 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
      >
        <CameraIcon className="h-4 w-4" />
        {uploading ? "Uploading..." : displayAvatar ? "Change photo" : "Add a photo"}
      </button>

      {uploadError && (
        <p className="text-red-400 text-xs mb-2">{uploadError}</p>
      )}

      <p className="text-white/40 text-xs mb-8">
        You can always change this later in Settings
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Actions */}
      <div className="flex items-center gap-4 w-full">
        <button
          onClick={onSkip}
          className="flex-1 py-2.5 text-sm text-white/60 hover:text-white/80"
        >
          Skip
        </button>
        <button
          onClick={onNext}
          className="flex-1 py-2.5 rounded-lg bg-primary hover:bg-primary-hover text-text-inverse text-sm font-medium"
        >
          Continue
        </button>
      </div>

      {cropUrl && (
        <AvatarCropModal
          imageUrl={cropUrl}
          onSave={handleCropSave}
          onClose={handleCropCancel}
        />
      )}
    </div>
  );
}
